import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef
} from 'react'
import {
  DayDataTypes,
  apiGetSeasonManualData,
  ApiGetSeasonManualDataPostData,
  EntityType,
  apiSaveData,
  ApiSaveDataDateDataList
} from 'src/api'
import { assertSaveDataSucceeded } from 'src/api/services/savedata/savedata'
import { useAbortController, useAppSelector } from 'src/hooks'
import { selectUser } from 'src/redux'
import { dateUtils } from 'src/utils/date.util'
import {
  RowLoaderControllerMap,
  CapacityRow,
  ChannelRow,
  LoadedCapacityRows,
  RateRow,
  RatesDataGridContextAction,
  RatesDataGridContextActionTypes,
  RatesDataGridContextState,
  RowType,
  SkeletonRow,
  LoadedChannelRows
} from './RatesDataGridContext.types'
import { TreeItemI } from 'src/components/customTree/CustomTree'
import { hotelDataUtils } from 'src/utils/api/hotelDataUtils'
import { customTreeUtils } from 'src/components'
import { RatesDataGridContextUtils } from './RatesDataGridContext.utils'
import { generateUUID } from 'src/utils/generateUid'
import { t } from 'i18next'
import {
  DEFAULT_LOAD_DAYS_QTY,
  RATE_DAY_VALUE_CONFIRM_LIMIT
} from 'src/applications/rates/Rates.constants'
import { useToastContext } from 'src/contexts/Toast.context'
import { useRatesContext } from 'src/applications/rates/Rates.context'
import {
  changeRowsDataType,
  closeAndRemoveChildRows,
  commitEditsToRows,
  discardRoomTypeEditedDays,
  removeCommittedEdits,
  replaceChildRows
} from './RatesDataGridContext.state'
import {
  getRateListFilter,
  haveSameRateFilterIds,
  normalizeRateFilterIds
} from '../../RatesDataGrid.filters'

interface RatesDataGridContextProps extends RatesDataGridContextState {
  refreshData: () => Promise<boolean>
  changeRateRowDataType: ({
    row,
    dataType,
  }: {
    row: RateRow | CapacityRow | ChannelRow
    dataType: keyof DayDataTypes
  }) => void
  openRateCapacityRows: ({ row }: { row: RateRow }) => Promise<void>
  closeRateCapacityRows: ({ row }: { row: RateRow }) => void
  openRateChannelRows: ({
    row
  }: {
    row: RateRow | CapacityRow
  }) => void
  closeRateChannelRows: ({
    row
  }: {
    row: RateRow | CapacityRow
  }) => void
  changeFilterVisibility: (id: string) => void
  changeTreeItems: (treeItems: TreeItemI[]) => void
  changeFilterRateIds: (rateIds: string[]) => void
  changeDateRange: (newDateRange: [Date, Date]) => void
  resetFilters: () => void
  setEditDateData: (params: {
    date: Date
    dayData: Partial<DayDataTypes>
    row: RateRow | ChannelRow | CapacityRow
  }) => void
  openMassiveModModal: (row?: RateRow | ChannelRow | CapacityRow) => void
  closeMassiveModModal: () => void
  saveEditedData: () => Promise<boolean>
  discardRoomTypeEdits: (roomTypeId: string) => void
  capacityLoaderControllers: React.MutableRefObject<RowLoaderControllerMap>
  channelLoaderControllers: React.MutableRefObject<RowLoaderControllerMap>
}

const RatesDataGridContext = createContext<RatesDataGridContextProps>({} as any)

export const ratesDataGridReducer = (
  state: RatesDataGridContextState,
  { type, payload }: RatesDataGridContextAction
): RatesDataGridContextState => {
  switch (type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: payload
      }
    case 'SET_INITIAL_STATE': {
      const { apiData } = payload
      const newRows = apiData
        ? RatesDataGridContextUtils.hotelDataListToRows({
            hotelDataList: apiData.hotelDataList
          })
        : []
      return {
        ...state,
        rows: newRows,
        ...payload
      }
    }
    case 'UPDATE_API_DATA': {
      return {
        ...state,
        ...payload
      }
    }
    case 'SET_DATE_RANGE': {
      return { ...state, dateRange: payload }
    }
    case 'SET_ROWS': {
      return {
        ...state,
        rows: payload
      }
    }
    case 'CHANGE_RATE_ROW_DATA_TYPE': {
      const { row, dataType } = payload
      return {
        ...state,
        rows: changeRowsDataType({
          rows: state.rows,
          roomTypeId: row.data.roomTypeId,
          dataType
        })
      }
    }
    case 'OPEN_RATE_ROW': {
      const rowIndex = state.rows.findIndex(
        (row) => row.id === payload.parentRowId
      )
      if (rowIndex < 0) return state
      const rowToOpen = state.rows[rowIndex] as RateRow
      const { type } = payload
      const openedRow = { ...rowToOpen }
      if (type === RowType.CAPACITY) {
        openedRow.showCapacities = true
      }
      if (type === RowType.CHANNEL) {
        openedRow.showChannels = true
      }
      return {
        ...state,
        rows: [
          ...state.rows.slice(0, rowIndex),
          openedRow,
          ...state.rows.slice(rowIndex + 1)
        ]
      }
    }
    case 'CLOSE_RATE_ROW': {
      return {
        ...state,
        rows: closeAndRemoveChildRows({
          currentRows: state.rows,
          parentRowId: payload.parentRowId,
          type: payload.type
        })
      }
    }
    case 'REPLACE_CHILD_ROWS': {
      return {
        ...state,
        rows: replaceChildRows({
          currentRows: state.rows,
          parentRowId: payload.parentRowId,
          type: payload.type,
          childRows: payload.rows
        })
      }
    }
    case 'SET_TREE_ITEMS':
      return {
        ...state,
        treeItems: payload
      }
    case 'RESET_FILTERS': {
      return {
        ...state,
        filterVisibilityId: '',
        treeItems: customTreeUtils.checkAll(state.treeItems, true),
        filterRateIds: []
      }
    }
    case 'SET_FILTER_RATE_IDS':
      return { ...state, filterRateIds: payload }
    case 'SET_FILTER_VISIBILITY_ID':
      return { ...state, filterVisibilityId: payload }
    case 'SET_EDITED_DATE': {
      const { row, date, dayData } = payload
      const dateKey = dateUtils.formatDateToString(date)
      const isOverLimit =
        dayData.price != null &&
        Number(dayData.price) > RATE_DAY_VALUE_CONFIRM_LIMIT
      const editedDaysListOverLimit = state.editedDaysListOverLimit.filter(
        (currentValue) =>
          currentValue.row.id !== row.id ||
          dateUtils.formatDateToString(currentValue.date) !== dateKey
      )
      return {
        ...state,
        editedDaysList: {
          ...state.editedDaysList,
          [row.id]: {
            ...state.editedDaysList[row.id],
            [dateKey]: dayData
          }
        },
        editedDaysListOverLimit: isOverLimit
          ? [...editedDaysListOverLimit, { date, row }]
          : editedDaysListOverLimit
      }
    }
    case 'DISCARD_ROOM_TYPE_EDITS': {
      return {
        ...state,
        editedDaysList: discardRoomTypeEditedDays(
          state.editedDaysList,
          payload
        ),
        editedDaysListOverLimit: state.editedDaysListOverLimit.filter(
          ({ row }) => row.data.roomTypeId !== payload
        )
      }
    }
    case 'COMMIT_EDITED_DAYS': {
      return {
        ...state,
        rows: commitEditsToRows(state.rows, payload),
        editedDaysList: removeCommittedEdits(state.editedDaysList, payload),
        editedDaysListOverLimit: state.editedDaysListOverLimit.filter(
          ({ row, date }) =>
            !payload[row.id]?.[dateUtils.formatDateToString(date)]
        )
      }
    }
    case 'SET_MASSIVE_MOD_MODAL': {
      return { ...state, massiveModModalOpen: payload }
    }

    default:
      return getInitialState()
  }
}

export const getInitialState = (): RatesDataGridContextState => ({
  dateRange: [
    new Date(),
    dateUtils.addToDate(new Date(), {
      value: DEFAULT_LOAD_DAYS_QTY - 1,
      type: 'days'
    })
  ],
  daysList: dateUtils.getDateList(
    new Date(),
    dateUtils.addToDate(new Date(), {
      value: DEFAULT_LOAD_DAYS_QTY - 1,
      type: 'days'
    })
  ),
  filterRateIds: [],
  loading: true,
  rows: [],
  treeItems: [],
  filterVisibilityId: '',
  apiData: undefined,
  editedDaysList: {},
  editedDaysListOverLimit: [],
  massiveModModalOpen: false
})

const RatesDataGridContextProvider: ReactFC = ({ children }) => {
  const toast = useToastContext()
  const { environmentId } = useRatesContext()

  // Capacities map
  const loadedCapacities = useRef<LoadedCapacityRows>({})
  const capacityLoaderControllers = useRef<RowLoaderControllerMap>({})

  // Channels map
  const loadedChannels = useRef<LoadedChannelRows>({})
  const channelLoaderControllers = useRef<RowLoaderControllerMap>({})

  const abortChildLoaders = useCallback(() => {
    Object.values(capacityLoaderControllers.current).forEach((controller) =>
      controller.abort()
    )
    Object.values(channelLoaderControllers.current).forEach((controller) =>
      controller.abort()
    )
    capacityLoaderControllers.current = {}
    channelLoaderControllers.current = {}
  }, [])

  // reducer
  const [state, dispatch] = useReducer(ratesDataGridReducer, getInitialState())
  const {
    dateRange,
    filterRateIds,
    rows,
    treeItems,
    filterVisibilityId,
    apiData,
    editedDaysList
  } = state

  // redux states
  const { serverUrl, token } = useAppSelector(selectUser)

  // custom hooks
  const { controller: fetchDataController, abortAndReplace } =
    useAbortController()

  // localHooks
  const isInitialCall = useRef(true)
  const isInitialFilterCall = useRef(true)

  const fetchData = useCallback(
    async (
      params: Partial<ApiGetSeasonManualDataPostData> & {
        controller?: AbortController
      } = {}
    ) => {
      const { controller: paramsController, ...postData } = params
      const res = await apiGetSeasonManualData({
        serverPath: serverUrl,
        data: {
          userToken: token,
          iniDate: dateUtils.formatDateToString(dateRange[0]),
          endDate: dateUtils.formatDateToString(dateRange[1]),
          environmentDataId: environmentId,
          filterVisibilityId,
          ...postData
        },
        controller: paramsController ?? fetchDataController
      })

      return res.data
    },
    [
      serverUrl,
      token,
      dateRange,
      filterVisibilityId,
      environmentId,
      fetchDataController
    ]
  )

  const updateData = useCallback(
    async ({ abortController }: { abortController: AbortController }) => {
      try {
        dispatch({
          type: 'SET_LOADING',
          payload: true
        })
        const checkedItems = customTreeUtils.getCheckedItems(treeItems)
        let checkedHotelIds: string[] | undefined
        let checkedRoomTypeIds: string[] | undefined

        checkedItems.forEach((treeItem) => {
          const { type } = treeItem

          if (type === EntityType.HotelAuxList) {
            checkedHotelIds
              ? checkedHotelIds.push(treeItem.id)
              : (checkedHotelIds = [treeItem.id])
            return
          }

          if (type === EntityType.RoomTypeAuxList) {
            checkedRoomTypeIds
              ? checkedRoomTypeIds.push(treeItem.id)
              : (checkedRoomTypeIds = [treeItem.id])
          }
        })

        const loadedChildrenCapacityIds: Set<string> = new Set()
        const childrenCapacityHotelIds: Set<string> = new Set()
        const childrenCapacityRoomTypeIds: Set<string> = new Set()
        const childrenCapacityRateIds: Set<string> = new Set()

        const childrenChannelHotelIds: Set<string> = new Set()
        const childrenChannelRoomTypeIds: Set<string> = new Set()
        const childrenChannelRateIds: Set<string> = new Set()
        const childrenChannelCapacityIds: Set<string> = new Set()

        const capacityOpenedRows: string[] = []
        const channelOpenedRows: string[] = []

        const defaultDataTypeMap: { [roomTypeId: string]: keyof DayDataTypes } =
          {}

        // Later should be improved and not iterate all rows
        rows.forEach((row) => {
          if ('dayDataType' in row && row.dayDataType !== 'price') {
            defaultDataTypeMap[row.data.roomTypeId] = row.dayDataType
          }
          if ('showCapacities' in row && row.showCapacities) {
            capacityOpenedRows.push(row.id)
          }
          if ('showChannels' in row && row.showChannels) {
            channelOpenedRows.push(row.id)
          }
        })

        Object.entries(loadedCapacities.current).forEach(
          ([fatherRowId, capacityRows]) => {
            capacityRows.forEach((capacityRow) => {
              const { capacityId: fatherCapacityId } =
                RatesDataGridContextUtils.getRowEntityIds(fatherRowId)
              const {
                data: { capacityId, hotelId, roomTypeId, rateId }
              } = capacityRow
              childrenCapacityHotelIds.add(hotelId)
              childrenCapacityRoomTypeIds.add(roomTypeId)
              childrenCapacityRateIds.add(rateId)
              loadedChildrenCapacityIds.add(capacityId)
              loadedChildrenCapacityIds.add(fatherCapacityId)
            })
          }
        )

        Object.entries(loadedChannels.current).forEach(
          ([fatherRowId, channelRows]) => {
            channelRows.forEach((channelRow) => {
              const {
                data: { hotelId, roomTypeId, rateId, capacityId }
              } = channelRow
              const { capacityId: fatherCapacityId } =
                RatesDataGridContextUtils.getRowEntityIds(fatherRowId)
              childrenChannelHotelIds.add(hotelId)
              childrenChannelRoomTypeIds.add(roomTypeId)
              childrenChannelRateIds.add(rateId)
              childrenChannelCapacityIds.add(capacityId)
              childrenChannelCapacityIds.add(fatherCapacityId)
            })
          }
        )

        const apiData = await fetchData({
          hotelList: checkedHotelIds,
          roomTypeList: checkedRoomTypeIds,
          rateList: getRateListFilter(filterRateIds),
          filterVisibilityId,
          controller: abortController
        })

        await Promise.all([
          (async () => {
            if (loadedChildrenCapacityIds.size > 0) {
              const childrenCapacityData = await fetchData({
                hotelList: Array.from(childrenCapacityHotelIds),
                roomTypeList: Array.from(childrenCapacityRoomTypeIds),
                rateList: Array.from(childrenCapacityRateIds),
                roomTypeCapacityList: Array.from(loadedChildrenCapacityIds),
                controller: abortController
              })

              const updatedCapacities: LoadedCapacityRows = {}

              RatesDataGridContextUtils.mapHotelDataListCapacityRows({
                hotelDataList: childrenCapacityData.hotelDataList,
                cb: (capacityRow) => {
                  const prevLoadedCapacities =
                    updatedCapacities[capacityRow.fatherId]
                  updatedCapacities[capacityRow.fatherId] = [
                    ...(prevLoadedCapacities ?? []),
                    {
                      ...capacityRow,
                      dayDataType:
                        defaultDataTypeMap[capacityRow.data.roomTypeId] ??
                        'price'
                    }
                  ]
                }
              })

              loadedCapacities.current = updatedCapacities
            }
          })(),
          (async () => {
            if (Object.keys(loadedChannels.current).length > 0) {
              const childrenChannelData = await fetchData({
                allChannels: true,
                hotelList: Array.from(childrenChannelHotelIds),
                roomTypeList: Array.from(childrenChannelRoomTypeIds),
                rateList: Array.from(childrenChannelRateIds),
                roomTypeCapacityList: Array.from(childrenChannelCapacityIds),
                controller: abortController
              })
              const updatedChannelRows: LoadedChannelRows = {}

              RatesDataGridContextUtils.mapHotelDataListChannelRows({
                hotelDataList: childrenChannelData.hotelDataList,
                cb: (channelRow) => {
                  const prevUpdatedChannelRows =
                    updatedChannelRows[channelRow.fatherId] ?? []
                  updatedChannelRows[channelRow.fatherId] = [
                    ...prevUpdatedChannelRows,
                    {
                      ...channelRow,
                      dayDataType:
                        defaultDataTypeMap[channelRow.data.roomTypeId] ??
                        'price'
                    }
                  ]
                }
              })

              loadedChannels.current = updatedChannelRows
            }
          })()
        ])

        const newRows = RatesDataGridContextUtils.hotelDataListToRows({
          hotelDataList: apiData.hotelDataList,
          defaultDayDataTypes: defaultDataTypeMap,
          loadedCapacityRows: Object.entries(loadedCapacities.current).reduce(
            (acc, [fatherRowId, capacityRows]) => {
              if (capacityOpenedRows.includes(fatherRowId))
                acc[fatherRowId] = capacityRows
              return acc
            },
            {} as LoadedCapacityRows
          ),
          loadedChannelRows: Object.entries(loadedChannels.current).reduce(
            (acc, [fatherRowId, channelRows]) => {
              if (channelOpenedRows.includes(fatherRowId)) {
                acc[fatherRowId] = channelRows
              }
              return acc
            },
            {} as LoadedChannelRows
          )
        })

        const daysList = dateUtils.getDateList(
          state.dateRange[0],
          state.dateRange[1]
        )

        dispatch({
          type: 'UPDATE_API_DATA',
          payload: {
            apiData,
            rows: newRows,
            daysList
          }
        })
        dispatch({
          type: 'SET_LOADING',
          payload: false
        })
        return true
      } catch (error: any) {
        if (error.name !== 'CanceledError') {
          dispatch({
            type: 'SET_LOADING',
            payload: false
          })
          console.log(error)
        }
        return false
      }
    },
    [
      treeItems,
      rows,
      fetchData,
      filterRateIds,
      filterVisibilityId,
      state.dateRange
    ]
  )

  const initialCall = useCallback(async (controller: AbortController) => {
    try {
      dispatch({
        type: 'SET_LOADING',
        payload: true
      })

      const data = await fetchData({ controller })

      // const filterVisibilityIdToFetch = data.filterList[2].id

      const apiData = await fetchData({
        roomTypeList: data.roomTypeAuxList?.map(({ id }) => id),
        controller
        // filterVisibilityId: filterVisibilityIdToFetch
      })

      const selectedEnvironment = apiData.environmentDataAuxList.find(
        ({ id }) => id === environmentId
      )!

      const treeItems = hotelDataUtils.environmentListToTreeItem({
        defaultChecked: true,
        environmentDataAuxList: [selectedEnvironment],
        hotelAuxList: apiData.hotelAuxList ?? [],
        roomTypes: apiData.roomTypeAuxList ?? [],
        zoneAuxList: apiData.zoneAuxList ?? []
      })

      dispatch({
        type: 'SET_INITIAL_STATE',
        payload: {
          apiData,
          treeItems
          // filterVisibilityId: filterVisibilityIdToFetch
        }
      })
    } catch (error) {
      if (
        !controller.signal.aborted &&
        (error as { name?: string }).name !== 'CanceledError'
      ) {
        console.log(error)
      }
    } finally {
      if (!controller.signal.aborted) {
        dispatch({
          type: 'SET_LOADING',
          payload: false
        })
        isInitialCall.current = false
      }
    }
  }, [environmentId, fetchData])

  //  useEffects
  useEffect(() => {
    isInitialCall.current = true
    isInitialFilterCall.current = true
    abortChildLoaders()
    const controller = abortAndReplace()
    initialCall(controller)
    return () => {
      loadedCapacities.current = {}
      loadedChannels.current = {}
      controller.abort()
      abortChildLoaders()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environmentId])

  useEffect(() => {
    const asyncFunc = async () => {
      if (isInitialCall.current) {
        return
      }
      if (isInitialFilterCall.current) {
        isInitialFilterCall.current = false
        return
      }
      abortChildLoaders()
      const newController = abortAndReplace()
      await updateData({ abortController: newController })
    }

    asyncFunc()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeItems, filterVisibilityId, filterRateIds, dateRange])

  useEffect(() => {
    if (!apiData?.rateAuxList) return
    const normalizedRateIds = normalizeRateFilterIds(
      filterRateIds,
      apiData.rateAuxList.map(({ id }) => id)
    )
    if (!haveSameRateFilterIds(filterRateIds, normalizedRateIds)) {
      dispatch({ type: 'SET_FILTER_RATE_IDS', payload: normalizedRateIds })
    }
  }, [apiData?.rateAuxList, filterRateIds])

  // Methods

  const refreshData = useCallback(async () => {
    abortChildLoaders()
    const controller = abortAndReplace()
    return await updateData({ abortController: controller })
  }, [abortAndReplace, abortChildLoaders, updateData])

  const changeRateRowDataType = useCallback(
    ({
      row,
      dataType
    }: RatesDataGridContextActionTypes['CHANGE_RATE_ROW_DATA_TYPE']) => {
      // changeLoadedCapacities
      loadedCapacities.current = Object.entries(
        loadedCapacities.current
      ).reduce((acc, [fatherId, capacityRows]) => {
        const { roomTypeId } =
          RatesDataGridContextUtils.getRowEntityIds(fatherId)
        if (roomTypeId === row.data.roomTypeId) {
          acc[fatherId] = capacityRows.map((capacityRow) => ({
            ...capacityRow,
            dayDataType: dataType
          }))
        } else {
          acc[fatherId] = capacityRows
        }
        return acc
      }, {} as LoadedCapacityRows)

      // changeLoadedChannels
      loadedChannels.current = Object.entries(loadedChannels.current).reduce(
        (acc, [fatherId, channelRows]) => {
          const { roomTypeId } =
            RatesDataGridContextUtils.getRowEntityIds(fatherId)
          if (roomTypeId === row.data.roomTypeId) {
            acc[fatherId] = channelRows.map((channelRow) => ({
              ...channelRow,
              dayDataType: dataType
            }))
          } else {
            acc[fatherId] = channelRows
          }
          return acc
        },
        {} as LoadedChannelRows
      )

      dispatch({
        type: 'CHANGE_RATE_ROW_DATA_TYPE',
        payload: {
          row,
          dataType
        }
      })
    },
    []
  )

  const openRateCapacityRows = useCallback(
    async ({ row }: { row: RateRow }) => {
      let requestController: AbortController | undefined
      try {
        dispatch({
          type: 'OPEN_RATE_ROW',
          payload: {
            parentRowId: row.id,
            type: RowType.CAPACITY
          }
        })
        if (loadedCapacities.current[row.id]) {
          dispatch({
            type: 'REPLACE_CHILD_ROWS',
            payload: {
              parentRowId: row.id,
              type: RowType.CAPACITY,
              rows: loadedCapacities.current[row.id]
            }
          })
        } else {
          const skeletonRows: SkeletonRow[] = []
          for (let i = 0; i < row.capacitiesQty; i++) {
            skeletonRows.push({
              fatherId: row.id,
              id: generateUUID(),
              type: RowType.CAPACITY_SKELETON
            })
          }
          dispatch({
            type: 'REPLACE_CHILD_ROWS',
            payload: {
              parentRowId: row.id,
              type: RowType.CAPACITY,
              rows: skeletonRows
            }
          })

          requestController = new AbortController()

          capacityLoaderControllers.current[row.id] = requestController

          const response = await fetchData({
            hotelList: [row.data.hotelId],
            roomTypeList: [row.data.roomTypeId],
            rateList: [row.data.rateId],
            roomTypeCapacityList: row.data.rateData.roomTypeCapacityList,
            controller: requestController
          })

          if (
            requestController.signal.aborted ||
            capacityLoaderControllers.current[row.id] !== requestController
          )
            return

          const hotelData = response.hotelDataList[0]

          const roomTypeData = hotelData.roomTypeDataList[0]

          const rateData = roomTypeData.rateDataList[0]

          const capacityRows = rateData.roomTypeCapacityDataList
            .slice(1)
            .map((roomTypeCapacity) =>
              RatesDataGridContextUtils.capacityToRow({
                capacityData: roomTypeCapacity,
                fatherId: row.id,
                hotelId: hotelData.id,
                rate: row.data.rateData,
                roomTypeId: roomTypeData.id,
                defaultDayDataType: row.dayDataType
              })
            )

          loadedCapacities.current[row.id] = capacityRows

          dispatch({
            type: 'REPLACE_CHILD_ROWS',
            payload: {
              parentRowId: row.id,
              type: RowType.CAPACITY,
              rows: capacityRows
            }
          })
        }
      } catch (error) {
        if ((error as { name?: string }).name !== 'CanceledError') {
          console.log(error)
        }
      } finally {
        if (capacityLoaderControllers.current[row.id] === requestController) {
          delete capacityLoaderControllers.current[row.id]
        }
      }
    },
    [fetchData]
  )

  const closeRateCapacityRows = useCallback(
    ({ row }: { row: RateRow }) => {
      capacityLoaderControllers.current[row.id]?.abort()
      delete capacityLoaderControllers.current[row.id]
      loadedCapacities.current[row.id]?.forEach((capacityRow) => {
        channelLoaderControllers.current[capacityRow.id]?.abort()
        delete channelLoaderControllers.current[capacityRow.id]
      })
      dispatch({
        type: 'CLOSE_RATE_ROW',
        payload: {
          parentRowId: row.id,
          type: RowType.CAPACITY
        }
      })
    },
    []
  )

  const openRateChannelRows = useCallback(
    async ({ row }: { row: RateRow | CapacityRow }) => {
      let requestController: AbortController | undefined
      try {
        dispatch({
          type: 'OPEN_RATE_ROW',
          payload: { parentRowId: row.id, type: RowType.CHANNEL }
        })

        const loadedRowChannels = loadedChannels.current[row.id]

        if (loadedRowChannels) {
          dispatch({
            type: 'REPLACE_CHILD_ROWS',
            payload: {
              parentRowId: row.id,
              type: RowType.CHANNEL,
              rows: loadedChannels.current[row.id]
            }
          })
        } else {
          const channelSkeletonRows: SkeletonRow[] = []

          for (let i = 0; i < row.channelsQty; i++) {
            channelSkeletonRows.push({
              fatherId: row.id,
              id: generateUUID(),
              type: RowType.CHANNEL_SKELETON
            })
          }

          dispatch({
            type: 'REPLACE_CHILD_ROWS',
            payload: {
              parentRowId: row.id,
              type: RowType.CHANNEL,
              rows: channelSkeletonRows
            }
          })

          requestController = new AbortController()

          channelLoaderControllers.current[row.id] = requestController

          const response = await fetchData({
            hotelList: [row.data.hotelId],
            roomTypeList: [row.data.roomTypeId],
            rateList: [row.data.rateId],
            roomTypeCapacityList: [row.data.capacityId],
            allChannels: true,
            controller: requestController
          })

          if (
            requestController.signal.aborted ||
            channelLoaderControllers.current[row.id] !== requestController
          )
            return

          const hotel = response.hotelDataList[0]
          const roomType = hotel.roomTypeDataList[0]
          const rate = roomType.rateDataList[0]
          const capacity = rate.roomTypeCapacityDataList[0]

          const channelRows = capacity.channelDataList
            .slice(1)
            .map((channel): ChannelRow => {
              return RatesDataGridContextUtils.channelToRow({
                channelData: channel,
                capacityId: capacity.id,
                fatherId: row.id,
                hotelId: hotel.id,
                rateId: row.data.rateId,
                roomTypeId: roomType.id,
                defaultDayDataType: row.dayDataType
              })
            })

          loadedChannels.current[row.id] = channelRows

          dispatch({
            type: 'REPLACE_CHILD_ROWS',
            payload: {
              parentRowId: row.id,
              type: RowType.CHANNEL,
              rows: channelRows
            }
          })
        }
      } catch (error) {
        if ((error as { name?: string }).name !== 'CanceledError') {
          console.log(error)
        }
      } finally {
        if (
          channelLoaderControllers.current[row.id] === requestController
        ) {
          delete channelLoaderControllers.current[row.id]
        }
      }
    },
    [fetchData]
  )

  const closeRateChannelRows = useCallback(
    ({ row }: { row: CapacityRow | RateRow }) => {
      channelLoaderControllers.current[row.id]?.abort()
      delete channelLoaderControllers.current[row.id]
      dispatch({
        type: 'CLOSE_RATE_ROW',
        payload: {
          parentRowId: row.id,
          type: RowType.CHANNEL
        }
      })
    },
    []
  )

  const changeDateRange = useCallback((newDateRange: [Date, Date]) => {
    dispatch({
      type: 'SET_DATE_RANGE',
      payload: newDateRange
    })
  }, [])

  // Filter methods

  const changeTreeItems = useCallback((newTreeItems: TreeItemI[]) => {
    dispatch({
      type: 'SET_TREE_ITEMS',
      payload: newTreeItems
    })
  }, [])

  const changeFilterRateIds = useCallback(
    (rateIds: string[]) => {
      const normalizedRateIds = normalizeRateFilterIds(
        rateIds,
        apiData?.rateAuxList?.map(({ id }) => id) ?? []
      )
      if (haveSameRateFilterIds(filterRateIds, normalizedRateIds)) return
      dispatch({
        type: 'SET_FILTER_RATE_IDS',
        payload: normalizedRateIds
      })
    },
    [apiData?.rateAuxList, filterRateIds]
  )

  const changeFilterVisibility = useCallback((visibilityId: string) => {
    dispatch({
      type: 'SET_FILTER_VISIBILITY_ID',
      payload: visibilityId
    })
  }, [])

  const resetFilters = useCallback(() => {
    dispatch({
      type: 'RESET_FILTERS',
      payload: undefined
    })
  }, [])

  // Edit Date Data

  const setEditDateData = useCallback(
    ({
      date,
      dayData,
      row
    }: {
      date: Date
      dayData: Partial<DayDataTypes>
      row: RateRow | ChannelRow | CapacityRow
    }) => {
      dispatch({
        type: 'SET_EDITED_DATE',
        payload: {
          date,
          dayData,
          row
        }
      })
    },
    []
  )

  const openMassiveModModal = useCallback(
    (row?: RateRow | ChannelRow | CapacityRow) => {
      dispatch({
        type: 'SET_MASSIVE_MOD_MODAL',
        payload: row || true
      })
    },
    []
  )

  const closeMassiveModModal = useCallback(() => {
    dispatch({
      type: 'SET_MASSIVE_MOD_MODAL',
      payload: false
    })
  }, [])

  const saveEditedData = useCallback(async () => {
    const editedDaysSnapshot = editedDaysList
    try {
      const dateDataList: ApiSaveDataDateDataList[] = []
      dispatch({ type: 'SET_LOADING', payload: true })
      Object.entries(editedDaysSnapshot).forEach(([rowId, dateValueMap]) => {
        Object.entries(dateValueMap).forEach(([date, editedValue]) => {
          const { rateId, channelId, roomTypeId, capacityId } =
            RatesDataGridContextUtils.getRowEntityIds(rowId)
          const editedDate = date
          dateDataList.push({
            channelList: [channelId],
            iniDate: editedDate,
            endDate: editedDate,
            rateList: [rateId],
            roomTypeList: [roomTypeId],
            roomTypeCapacityId: capacityId,
            ...editedValue
          })
        })
      })
      const response = await apiSaveData({
        serverPath: serverUrl,
        data: {
          environmentDataId: environmentId!,
          userToken: token,
          dateDataList
        }
      })
      assertSaveDataSucceeded(response)

      loadedCapacities.current = Object.entries(
        loadedCapacities.current
      ).reduce<LoadedCapacityRows>((committedRows, [parentId, capacityRows]) => {
        committedRows[parentId] = commitEditsToRows(
          capacityRows,
          editedDaysSnapshot
        ) as CapacityRow[]
        return committedRows
      }, {})
      loadedChannels.current = Object.entries(loadedChannels.current).reduce<
        LoadedChannelRows
      >((committedRows, [parentId, channelRows]) => {
        committedRows[parentId] = commitEditsToRows(
          channelRows,
          editedDaysSnapshot
        ) as ChannelRow[]
        return committedRows
      }, {})
      dispatch({ type: 'COMMIT_EDITED_DAYS', payload: editedDaysSnapshot })
      toast.success({ message: t('alerts.data_saved') })
      const refreshed = await refreshData()
      if (!refreshed) {
        toast.warning({ message: t('alerts.data_saved_refresh_failed') })
      }
      return true
    } catch (error) {
      toast.error({
        message: t('alerts.An_error_has_ocurred_try_again')
      })
      return false
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [editedDaysList, environmentId, refreshData, serverUrl, toast, token])

  const discardRoomTypeEdits = useCallback((roomTypeId: string) => {
    dispatch({ type: 'DISCARD_ROOM_TYPE_EDITS', payload: roomTypeId })
  }, [])

  const providerValue: RatesDataGridContextProps = useMemo(
    () => ({
      ...state,
      apiData,
      changeRateRowDataType,
      changeFilterVisibility,
      changeTreeItems,
      changeFilterRateIds,
      changeDateRange,
      openRateChannelRows,
      closeRateChannelRows,
      openRateCapacityRows,
      closeRateCapacityRows,
      resetFilters,
      setEditDateData,
      closeMassiveModModal,
      openMassiveModModal,
      saveEditedData,
      discardRoomTypeEdits,
      refreshData,
      capacityLoaderControllers,
      channelLoaderControllers
    }),
    [
      apiData,
      changeDateRange,
      changeFilterRateIds,
      changeFilterVisibility,
      changeRateRowDataType,
      changeTreeItems,
      closeMassiveModModal,
      closeRateCapacityRows,
      closeRateChannelRows,
      saveEditedData,
      discardRoomTypeEdits,
      openMassiveModModal,
      openRateCapacityRows,
      openRateChannelRows,
      refreshData,
      resetFilters,
      setEditDateData,
      state
    ]
  )

  return (
    <RatesDataGridContext.Provider value={providerValue}>
      {children}
    </RatesDataGridContext.Provider>
  )
}
export default RatesDataGridContextProvider

export const useRatesDataGridContext = () => useContext(RatesDataGridContext)
